import BasicLink from "@components/core/links/BasicLink";
import CheckIcon from "@static/core/icons/icon-check.svg?react";
import DownloadIcon from "@static/core/icons/icon-download.svg?react";
import ViewIcon from "@static/core/icons/icon-view.svg?react";
import {
  Document,
  InitialFields,
  Signatory,
  Signature,
} from "@type/core/dashboard/dashboard.types";
import { TransactionDetail } from "@type/core/transaction/transaction.types";
import { getToken } from "@utils/helpers/api.helper";
import { Collapse } from "antd";
import { ColumnsType } from "antd/lib/table/interface";
import i18n from "i18next";
import SignatureHelper from "@utils/helpers/signature-helper";

const { Panel } = Collapse;

const getSignatorySignaturesCount = (
  allSignaturesOfCurrentFile: Signature[],
  signatory: Signatory,
): number => {
  return allSignaturesOfCurrentFile.filter((signature: Signature) => {
    return signature.email === signatory.email;
  }).length;
};
export const columns = (
  transaction: TransactionDetail,
  anySigned: boolean,
  authorized: boolean,
  showDownload: boolean,
  hasNotSigned: boolean,
  isAttachmentsPanel: boolean,
): ColumnsType<Document> => [
  {
    dataIndex: "fileName",
    render: (fileName: string, record: Document) => {
      const signatureEmails = new Set(
        record.signatures.map((signature) => signature.email),
      );
      const documentSignatories = transaction.signatories?.filter((signatory) =>
        signatureEmails.has(signatory.email),
      );
      const displayNotSignedByEveryoneWarning =
        documentSignatories?.length !== transaction.signatories?.length;

      const isMultiSignature = SignatureHelper.areSignatureMultiple(
        record.signatures,
      );

      return (
        <div
          className="d-flex align-items-center gap-16"
          data-test={"table-item"}
        >
          <span className="flex-grow-1">
            {fileName}
            {record.fileSize && (
              <span className="ml-8 text-nowrap"> ({record.fileSize} Ko)</span>
            )}
          </span>
          {!isAttachmentsPanel &&
            transaction.signatureRequirementMode === "SOME" && (
              <span>
                {displayNotSignedByEveryoneWarning && (
                  <Collapse
                    expandIconPosition="end"
                    size="small"
                    bordered={false}
                    className={"custom-collapse font-size-14"}
                  >
                    <Panel
                      header={
                        <span className="text-dark-grey">
                          {i18n.t("process.signature.documentSignedBySome", {
                            count: documentSignatories?.length,
                          })}
                        </span>
                      }
                      key="1"
                    >
                      <div className="d-flex flex-column align-items-end justify-content-end">
                        {documentSignatories?.map((signatory) => (
                          <span className="text-nowrap" key={signatory.email}>
                            {signatory.firstname} {signatory.lastname}
                            {isMultiSignature &&
                              ` (x${getSignatorySignaturesCount(record.signatures, signatory)})`}
                          </span>
                        ))}
                      </div>
                    </Panel>
                  </Collapse>
                )}
                {!displayNotSignedByEveryoneWarning && !isMultiSignature && (
                  <span className="text-right w-100 text-nowrap flex-shrink-0">
                    {i18n.t("process.signature.documentSignedByAll")}
                  </span>
                )}
                {!displayNotSignedByEveryoneWarning && isMultiSignature && (
                  <Collapse
                    expandIconPosition="end"
                    size="small"
                    bordered={false}
                    className={"custom-collapse font-size-14"}
                  >
                    <Panel
                      header={
                        <span className="text-dark-grey">
                          {i18n.t("process.signature.documentSignedByAll")}
                        </span>
                      }
                      key="1"
                    >
                      <div className="d-flex flex-column align-items-end justify-content-end">
                        {documentSignatories?.map((signatory) => (
                          <span className="text-nowrap" key={signatory.email}>
                            {signatory.firstname} {signatory.lastname}
                            {isMultiSignature &&
                              ` (x${getSignatorySignaturesCount(record.signatures, signatory)})`}
                          </span>
                        ))}
                      </div>
                    </Panel>
                  </Collapse>
                )}
              </span>
            )}

          {!isAttachmentsPanel &&
            transaction.signatureRequirementMode === "ALL" &&
            !displayNotSignedByEveryoneWarning && (
              <span>
                {isMultiSignature && (
                  <Collapse
                    expandIconPosition="end"
                    size="small"
                    bordered={false}
                    className={"custom-collapse font-size-14"}
                  >
                    <Panel
                      header={
                        <span className="text-dark-grey">
                          {i18n.t("process.signature.documentSignedByAll")}
                        </span>
                      }
                      key="1"
                    >
                      <div className="d-flex flex-column align-items-end justify-content-end">
                        {documentSignatories?.map((signatory) => (
                          <span className="text-nowrap" key={signatory.email}>
                            {signatory.firstname} {signatory.lastname}
                            {isMultiSignature &&
                              ` (x${getSignatorySignaturesCount(record.signatures, signatory)})`}
                          </span>
                        ))}
                      </div>
                    </Panel>
                  </Collapse>
                )}
                {!isMultiSignature && transaction.type === "signature" && (
                  <span className="text-right w-100 text-nowrap flex-shrink-0">
                    {i18n.t("process.signature.documentSignedByAll")}
                  </span>
                )}
              </span>
            )}
        </div>
      );
    },
  },
  {
    dataIndex: "initialFields",
    render: (initialFields: InitialFields) => {
      if (initialFields) {
        return (
          <div
            className="d-flex align-items-center gap-16"
            data-test={"table-item"}
          >
            <span className="flex-grow-1">
              {i18n.t("transaction.detail.withInitialFields")}
            </span>
          </div>
        );
      } else {
        return null;
      }
    },
  },
  ...(transaction.signatureRequirementMode === "SOME" && anySigned
    ? [
        {
          dataIndex: "signatures",
          width: 54,
          render: (signatures: Signature[]) => {
            return signatures &&
              signatures.every((signature) => signature.status === "signed") ? (
              <CheckIcon className="text-success icon-30" />
            ) : (
              <></>
            );
          },
        },
      ]
    : []),
  ...(authorized
    ? [
        {
          dataIndex: "path",
          width: 54,
          render: (path: string, record: Document) => {
            const viewUrl = path + "?token=" + getToken();
            const notSigned =
              hasNotSigned &&
              record.signatures &&
              record.signatures.some(
                (signature) => signature.status === "notsigned",
              );
            return path ? (
              <BasicLink
                to={viewUrl + "&download=false"}
                target="_blank"
                title={i18n.t("buttons.open")}
              >
                <ViewIcon
                  className={`icon-30 ${
                    notSigned ? "text-danger" : "text-black"
                  }`}
                />
              </BasicLink>
            ) : (
              <></>
            );
          },
        },
      ]
    : []),
  ...(authorized && !(transaction && !showDownload)
    ? [
        {
          dataIndex: "path",
          width: 54,
          render: (path: string, record: Document) => {
            const viewUrl = path + "?token=" + getToken();
            const notSigned =
              hasNotSigned &&
              record.signatures &&
              record.signatures.some(
                (signature) => signature.status === "notsigned",
              );
            return path ? (
              <BasicLink
                to={viewUrl + "&download=true"}
                target="_blank"
                download={record.fileName}
                title={i18n.t("buttons.download")}
              >
                <DownloadIcon
                  className={`icon-30 ${
                    notSigned ? "text-danger" : "text-black"
                  }`}
                />
              </BasicLink>
            ) : (
              <></>
            );
          },
        },
      ]
    : []),
];
